import axios from "axios";
import apiAi from "../utils/apiAi";

export const BASE_URL = `${process.env.REACT_APP_BASE_URL}`;

axios.defaults.baseURL = BASE_URL;

/**
 * Fetches assessments from the server.
 * @returns {Promise<Array>} A promise that resolves to an array of assessments.
 */
export async function getAssessments() {
  const token = localStorage.getItem("token");

  return axios
    .get(
      "assessments?is_paginate=0&order=asc&order-column=order_no&is_active_only=10",
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching assessments:", error);
      throw error;
    });
}

export async function getAssessmentSummary(userId) {
  const token = localStorage.getItem("token");
  return axios
    .get(`get-assessment-summary/${userId}`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching assessment summary:", error);
      throw error;
    });
}

export async function uploadFile(
  file,
  fileType,
  runId,
  assessmentId,
  onProgress,
  fileName,
) {
  const token = localStorage.getItem("token");

  // Get presigned URL from the server
  const res = await axios.post(
    "get-s3-token-for-assessment-upload",
    {
      runId,
      assessmentId,
      fileType,
      fileName,
    },
    {
      headers: {
        Authorization: "Bearer " + token,
      },
    }
  );

  const presignedUploadUrl = res.data.data;

  const formData = new FormData();
  Object.entries(presignedUploadUrl.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append("file", file);
  formData.append("folder_name", "assessments");

  await axios
    .post(presignedUploadUrl.url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const percentage = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        if (onProgress) {
          onProgress(percentage);
        }
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error uploading file:", error);
      throw error;
    });

  return (
    presignedUploadUrl.url + encodeURIComponent(presignedUploadUrl.fields.key)
  );
}

export async function analyzeImage(url, assessmentType) {
  const res = await apiAi.post("/single-pose/", {
    image_url: url,
    pose_type: assessmentType,
  });

  return res.data;
}

export async function getAssessmentRunId() {
  const token = localStorage.getItem("token");

  return axios
    .get("assessment-runs", {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error generating assessment id:", error);
      throw error;
    });
}

export async function generateAssessmentRunId() {
  const token = localStorage.getItem("token");

  return axios
    .post(
      "assessment-runs",
      { progress: "assessment" },
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error generating assessment id:", error);
      throw error;
    });
}

export async function getLatestAssessmentRunId() {
  const res = await getAssessmentRunId();
  return res?.status && res?.data?.length > 0 ? res.data[0].id : null;
}

export async function getProgramAssignment(runId) {
  const token = localStorage.getItem("token");

  return axios
    .get(`assessment-runs/${runId}/assignment`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching program assignment:", error);
      throw error;
    });
}

export async function createAssessmentStepWithImage(data) {
  const token = localStorage.getItem("token");

  return axios
    .post("create-assessment-step-with-image", data, {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error creating assessment step with image:", error);
      throw error;
    });
}

export async function createAssessmentStep(data) {
  const token = localStorage.getItem("token");

  return axios
    .post("create-assessment-step", data, {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error creating assessment step:", error);
      throw error;
    });
}

export async function getAssessmentData(userId) {
  const token = localStorage.getItem("token");

  return axios
    .get(`get-assessments-data-v2/${userId}`, {
      headers: {
        Authorization: "Bearer " + token,
      },
    })
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error fetching assessment data:", error);
      throw error;
    });
}

export async function resetAssessment(userId) {
  const token = localStorage.getItem("token");

  return axios
    .put(
      `reset-assessments/${userId}`,
      { userId },
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      console.error("Error resetting assessment:", error);
      throw error;
    });
}

export async function finalizeAssessment() {
  const token = localStorage.getItem("token");

  try {
    const runId = await getLatestAssessmentRunId();
    const res = await axios.post(
      "/finalize-assessment",
      { runId },
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      },
    );

    return res.data;
  } catch (error) {
    throw error?.response?.data || { message: "Unknown error" };
  }
}

export async function assessmentDiscrepancyCheck() {
  const token = localStorage.getItem("token");
  const runId = await getLatestAssessmentRunId();

  return axios.post(
    "/is-discrepancy-check-done/",
    {
      runId,
    },
    {
      headers: {
        Authorization: "Bearer " + token,
      },
    },
  );
}
